import React from 'react';
import { Directionality, getDirectionalityString } from './properties_common';

/**
 * Helper function for non-JSX code to get a property tooltip element.
 * @param {string} heading
 * @param {{
 *     label, description, unit, directionality, unitType, range, goodRange, r2,
 * }} propInfo
 * @returns {JSX.Element}
 */
export function getPropertyTooltip(heading, propInfo) {
    return (
        <PropertyTooltip heading={heading} propInfo={propInfo} />
    );
}

/**
 * The lines of a property's tooltip, keyed by kind (heading, label, description, unit, ...).
 * @param {string} heading
 * @param {{
 *     label, description, unit, directionality, unitType, range, goodRange, r2,
 * }} propInfo
 * @returns {Object<string, string>}
 */
function getPropertyTooltipAttributes(heading, propInfo) {
    const dir = propInfo.directionality && propInfo.directionality !== Directionality.NOT_APPLICABLE
        ? propInfo.directionality
        : null;
    const dirString = dir ? getDirectionalityString(dir, propInfo.unitType, propInfo.unit) : null;
    // A list of [kind, text-or-falsy] pairs, then keep only the ones with something to say —
    // reads as "here are the possible lines, drop the empty ones" in one pass, rather than 8
    // near-identical conditional spreads. `goodRange`/`r2` deliberately check `!= null` (not bare
    // truthiness) before the `&&`, so a legitimate `0` still produces its line: the `!= null`
    // check resolves to a boolean first, and `&&` short-circuits on THAT, not on whether the
    // resulting number is itself truthy.
    return Object.fromEntries([
        ['heading', heading],
        ['label', propInfo.label],
        ['description', propInfo.description],
        ['unit', propInfo.unit && `Unit: ${propInfo.unit}`],
        ['directionality', dirString && `Directionality: ${dirString}`],
        ['range', propInfo.range && `Range: ${propInfo.range}`],
        ['goodRange', propInfo.goodRange != null && `Good Range: ${propInfo.goodRange}`],
        ['r2', propInfo.r2 != null && `Training Confidence (r²): ${propInfo.r2}`],
    ].filter(([, text]) => text));
}

/**
 * Whether a property's tooltip says anything beyond identifying it (its name and, if given, the
 * heading grouping it) — a tooltip that would show only those is noise, so callers can skip it.
 * Takes the same `heading` the matching {@link getPropertyTooltip} call would use, so this
 * answers exactly what that call would render, not an approximation of it.
 * @param {object} propInfo Same shape as for {@link getPropertyTooltip}.
 * @param {string} [heading]
 * @returns {boolean}
 */
export function hasPropertyTooltipDetail(propInfo, heading) {
    if (!propInfo) return false;
    return Object.keys(getPropertyTooltipAttributes(heading, propInfo))
        .some((kind) => kind !== 'label' && kind !== 'heading');
}

/**
 * React component to display a tooltip for a property, given its info.
 * @param {{
 * heading: string,
 * propInfo: {
 *     label, description, unit, directionality, unitType, range, goodRange, r2,
 * }}} props
 * @returns {JSX.Element}
 */

export function PropertyTooltip({ heading, propInfo }) {
    if (!propInfo || Object.keys(propInfo).length === 0) {
        return false;
    }

    return (
        <AttributeListDisplay attributes={getPropertyTooltipAttributes(heading, propInfo)} />
    );
}

/**
 * React component to display items in a <p>, separated by <br>.
 * Object keys are only used as React keys, the full attribute text needs to be in Object values.
 * @param {{ attributes }} props
 * @returns {JSX.Element}
 */
export function AttributeListDisplay({ attributes }) {
    const lines = [];
    for (const [key, value] of Object.entries(attributes)) {
        if (typeof value === 'string') {
            lines.push(...(
                value.split('\n').map((line, index) => ({ key, line: index, value: line }))
            ));
        } else {
            lines.push({ key, line: 0, value });
        }
    }
    return (
        <p>
            {lines.map(
                ({ key, line, value }, i) => (
                    <React.Fragment key={`${key}-line-${line}`}>
                        {value}
                        {(i < lines.length - 1) && <br />}
                    </React.Fragment>
                )
            )}
        </p>
    );
}
